/**
 * Unified tracking helper.
 *
 * Fans a single event to:
 *   1. Google Analytics (via existing utils/analytics.trackEvent)
 *   2. Mailchimp Connected Sites browser tag (utils/mailchimp)
 *   3. Backend marketing API (services/marketing) — only when authenticated
 *      AND the event is in the allow-list of client-triggerable events.
 *
 * Callers should use this helper for any product analytics; never call
 * the underlying services directly from page code.
 */
import { trackEvent as gaTrackEvent } from "@/utils/analytics";
import { mailchimpTrack } from "@/utils/mailchimp";
import { loadStore } from "@/utils/localStorage";
import { isClientAuthenticated } from "@/utils/authState";
import marketingService, {
  type MarketingEventName,
} from "@/services/marketing";
import { AnalyticsEvent, AnalyticsEventData } from "@/types/analytics";

const CLIENT_TRIGGERABLE_EVENTS: ReadonlyArray<MarketingEventName> = [
  "user_explored_properties",
  "user_checked_weekly_deals",
  "user_logged_in",
  "user_explored_managed_props",
  "user_viewed_property",
];

function isClientTriggerable(name: string): name is MarketingEventName {
  return (CLIENT_TRIGGERABLE_EVENTS as ReadonlyArray<string>).includes(name);
}

/**
 * True when the current visitor has an authenticated session.
 *
 * Auth is cookie/httpOnly based. The AuthProvider keeps a module-level flag in
 * sync (utils/authState) as the primary signal; the JS-visible `is_logged_in`
 * login cookie and the legacy sessionStorage token are kept as fallbacks so a
 * check that races the auth boot (flag still false) still resolves.
 */
function hasAuthToken(): boolean {
  if (typeof window === "undefined") return false;
  if (isClientAuthenticated()) return true;

  // Fallback: JS-visible login cookies set alongside the httpOnly session.
  try {
    if (
      document.cookie.includes("is_logged_in=true") ||
      document.cookie.includes("admin_is_logged_in=true")
    ) {
      return true;
    }
  } catch {
    // ignore cookie access errors
  }

  // Fallback: legacy token store (pre-httpOnly-cookie sessions).
  const store = loadStore();
  return !!(store && (store as any).token);
} // 1. Extract the strict literal types directly from your union
type AnalyticsCategory = AnalyticsEventData["event_category"];
// This correctly evaluates to: 'property' | 'search' | 'contact' | 'engagement'

// 2. Safely omit it from props so it doesn't accidentally override things
type AnalyticsProps = Record<string, string | number | boolean>;
type SafeAnalyticsProps = Omit<AnalyticsProps, "event_category">;

export interface TrackOptions {
  category?: AnalyticsCategory;
  skipBackend?: boolean;
}

export function track(
  name: string,
  props: SafeAnalyticsProps = {},
  options: TrackOptions = {},
): void {
  // 1) GA — always
  try {
    // We use "as AnalyticsEventData" to tell TS that this combined object
    // satisfies the shape expected by gaTrackEvent.
    gaTrackEvent(name, {
      ...props,
      event_category: options.category ?? ("engagement" as const),
    } as AnalyticsEventData);
  } catch {
    /* noop */
  }

  // 2) Mailchimp browser tag — always
  try {
    mailchimpTrack(name, props);
  } catch {
    /* noop */
  }

  // 3) Backend marketing service — only authed + allow-listed
  if (!options.skipBackend && hasAuthToken() && isClientTriggerable(name)) {
    void marketingService.trackEvent(name, props);
  }
}

export default track;
